import { type JSX, useEffect } from 'react';

interface Shortcut {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  group: string;
  items: Shortcut[];
}

const SHORTCUTS: readonly ShortcutGroup[] = [
  {
    group: 'Editing',
    items: [
      { keys: ['⌘', 'Z'], description: 'Undo last config change' },
      { keys: ['⌘', '⇧', 'Z'], description: 'Redo' },
      { keys: ['⌘', 'Y'], description: 'Redo (alt)' },
    ],
  },
  {
    group: 'Navigation',
    items: [
      { keys: ['Tab'], description: 'Move focus forward' },
      { keys: ['⇧', 'Tab'], description: 'Move focus back' },
      { keys: ['Enter'], description: 'Activate the focused button' },
      { keys: ['Space'], description: 'Toggle the focused checkbox / button' },
    ],
  },
  {
    group: 'Preview pane',
    items: [
      { keys: ['←', '→'], description: 'Resize preview pane (focus the splitter handle first)' },
      { keys: ['⇧', '←', '→'], description: 'Resize preview pane in 4× steps' },
    ],
  },
  {
    group: 'Help',
    items: [
      { keys: ['?'], description: 'Open this cheatsheet' },
      { keys: ['Esc'], description: 'Close this cheatsheet' },
    ],
  },
];

export interface ShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal cheatsheet for keyboard shortcuts. Opened with `?` (handled in
 * App.tsx) or the ? button on the Toolbar. Closes on Esc, backdrop click,
 * or × button.
 *
 * Note on modifiers: we render ⌘ universally rather than detecting OS at
 * mount. Reason: Ctrl maps to the same keydown handler in App.tsx, so the
 * spelling difference is cosmetic, and a single string keeps the layout
 * stable when the cheatsheet is screenshotted or shared.
 */
export function ShortcutsHelp({ open, onClose }: ShortcutsHelpProps): JSX.Element | null {
  // Esc-to-close. Effect only attaches a listener while the modal is open
  // so the global ? handler in App.tsx isn't double-fired.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="shortcuts-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcuts-title"
      onClick={onClose}
      data-testid="shortcuts-overlay"
    >
      <div
        className="shortcuts-modal"
        // Stop clicks inside the modal from closing it.
        onClick={(e) => e.stopPropagation()}
      >
        <header className="shortcuts-modal__head">
          <h2 id="shortcuts-title" className="shortcuts-modal__title">
            Keyboard shortcuts
          </h2>
          <button
            type="button"
            className="shortcuts-modal__close"
            onClick={onClose}
            aria-label="Close shortcuts"
            title="Close (Esc)"
          >
            ×
          </button>
        </header>

        <div className="shortcuts-modal__body">
          {SHORTCUTS.map((g) => (
            <section key={g.group} className="shortcuts-group">
              <h3 className="shortcuts-group__title">{g.group}</h3>
              <ul className="shortcuts-group__list">
                {g.items.map((s) => (
                  <li key={s.description} className="shortcuts-row">
                    <span className="shortcuts-row__keys">
                      {s.keys.map((k, i) => (
                        <span key={i}>
                          <kbd className="shortcuts-row__kbd">{k}</kbd>
                          {i < s.keys.length - 1 && <span className="shortcuts-row__plus">+</span>}
                        </span>
                      ))}
                    </span>
                    <span className="shortcuts-row__desc">{s.description}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <footer className="shortcuts-modal__foot">
          On Windows / Linux: <kbd className="shortcuts-row__kbd">Ctrl</kbd> instead of{' '}
          <kbd className="shortcuts-row__kbd">⌘</kbd>.
        </footer>
      </div>
    </div>
  );
}
